import React, { useState, useEffect, useCallback } from 'react';
import { sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { auth, db } from '../../firebase.config';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import '../../styles/kyro-design.css';

const EMAIL_LINK_KEY = 'emailForSignIn';

const InvestorLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [completingSignIn, setCompletingSignIn] = useState(false);
  const navigate = useNavigate();

  const completeSignIn = useCallback((emailForSignIn: string) => {
    setCompletingSignIn(true);
    setError('');
    signInWithEmailLink(auth, emailForSignIn, window.location.href)
      .then(() => {
        window.localStorage.removeItem(EMAIL_LINK_KEY);
        window.history.replaceState({}, document.title, window.location.pathname);
        navigate('/investor-dashboard');
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Invalid or expired link. Please request a new one.');
        setCompletingSignIn(false);
      });
  }, [navigate]);

  useEffect(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      const emailForSignIn = window.localStorage.getItem(EMAIL_LINK_KEY);
      if (emailForSignIn) {
        completeSignIn(emailForSignIn);
      } else {
        setError('You opened this link on a different device. Enter your email below to complete sign-in.');
      }
    }
  }, [completeSignIn]);

  const handleRequestLink = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError('Please enter your email address.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const whitelistDoc = await getDoc(doc(db, 'investorEmails', 'whitelist'));
      const emails: string[] = whitelistDoc.data()?.emails || [];
      if (!emails.includes(trimmed)) {
        setError('This email is not authorized to access the investor portal. Please contact your relationship manager.');
        setLoading(false);
        return;
      }
      const actionCodeSettings = {
        url: `${window.location.origin}/investor-login`,
        handleCodeInApp: true,
      };
      await sendSignInLinkToEmail(auth, trimmed, actionCodeSettings);
      window.localStorage.setItem(EMAIL_LINK_KEY, trimmed);
      setLinkSent(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to send login link. Please try again.';
      const isAuthConfig = /permission|operation-not-allowed|provider.*disabled/i.test(msg);
      setError(isAuthConfig
        ? 'Login link could not be sent. Email link sign-in may need to be enabled in Firebase Console. Contact compliance@kyrocapital.in for assistance.'
        : msg);
    } finally {
      setLoading(false);
    }
  };

  const needsEmailForLink = isSignInWithEmailLink(auth, window.location.href) && !window.localStorage.getItem(EMAIL_LINK_KEY);

  if (completingSignIn) {
    return (
      <div className="page">
        <div className="login-hero-min">
          <div className="hero-grid" />
          <div className="hero-glow" />
          <div className="hero-inner" style={{ textAlign: 'center', maxWidth: 'none' }}>
            <h1 style={{ fontSize: 'clamp(1.8rem, 3vw, 2.4rem)', marginBottom: '0.5rem' }}>Completing sign-in...</h1>
            <p className="hero-sub" style={{ margin: '0 auto' }}>Please wait.</p>
          </div>
        </div>
      </div>
    );
  }

  if (needsEmailForLink) {
    return (
      <div className="page">
        <div className="login-hero-min">
          <div className="hero-grid" />
          <div className="hero-glow" />
          <div className="hero-inner" style={{ textAlign: 'center', maxWidth: 'none' }}>
            <h1 style={{ fontSize: 'clamp(1.8rem, 3vw, 2.4rem)', marginBottom: '0.5rem' }}>Complete sign-in</h1>
            <p className="hero-sub" style={{ marginBottom: 0, margin: '0 auto' }}>Enter the email address where you received the login link</p>
            <div className="login-card-min">
              {error && <div style={{ marginBottom: '1rem', fontSize: '0.85rem', color: 'var(--gray)' }}>{error}</div>}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const val = (e.currentTarget.elements.namedItem('email') as HTMLInputElement)?.value?.trim();
                  if (val) completeSignIn(val);
                }}
                className="c-form"
              >
                <input className="c-input" type="email" name="email" aria-label="Email to complete sign-in" placeholder="you@company.com" required />
                <button type="submit" className="c-submit">Complete sign-in</button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="login-hero-min">
        <div className="hero-grid" />
        <div className="hero-glow" />
        <div className="hero-inner" style={{ textAlign: 'center', maxWidth: 'none' }}>
          <h1 style={{ fontSize: 'clamp(1.8rem, 3vw, 2.4rem)', marginBottom: '0.5rem' }}>Investor Login</h1>
          <p className="hero-sub" style={{ marginBottom: 0, margin: '0 auto' }}>
            {linkSent ? 'Check your email for the login link' : 'Enter your email to receive a secure login link'}
          </p>
          <div className="login-card-min">
            {linkSent ? (
              <>
                <p style={{ fontSize: '0.9rem', color: 'var(--gray)', marginBottom: '1rem' }}>
                  We sent a login link to <strong>{email}</strong>. Click the link in that email to sign in.
                </p>
                <p style={{ fontSize: '0.85rem', color: 'var(--gray)' }}>
                  Didn&apos;t receive it? Check your spam folder or{' '}
                  <button
                    type="button"
                    onClick={() => { setLinkSent(false); setEmail(''); }}
                    style={{ background: 'none', border: 'none', color: 'var(--blue)', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit' }}
                  >
                    try again
                  </button>
                </p>
              </>
            ) : (
              <form onSubmit={handleRequestLink} className="c-form">
                <input
                  className="c-input"
                  type="email"
                  aria-label="Email address for login link"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  required
                />
                <button type="submit" className="c-submit" disabled={loading}>
                  {loading ? 'Sending link...' : 'Send login link'}
                </button>
              </form>
            )}
            {error && <div style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#DC2626' }} role="alert" aria-live="assertive">{error}</div>}
            <p style={{ fontSize: '0.75rem', color: 'var(--gray)', marginTop: '1.2rem', lineHeight: 1.6 }}>
              For assistance, contact <a href="mailto:compliance@kyrocapital.in" style={{ color: 'var(--blue)' }}>compliance@kyrocapital.in</a> or <a href="tel:+917611111102" style={{ color: 'var(--blue)' }}>76 111 111 02</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestorLogin;
